import { UserRole } from '@/features/auth/types'

/** Espejo de app/core/permissions.py — mantener sincronizado si el backend cambia. */
export const Permission = {
  COMPANIES_READ: 'companies:read',
  COMPANIES_MANAGE: 'companies:manage',
  SOURCES_READ: 'sources:read',
  SOURCES_MANAGE: 'sources:manage',
  DOCUMENTS_READ: 'documents:read',
  DOCUMENTS_MANAGE: 'documents:manage',
  REPORTS_READ: 'reports:read',
  REPORTS_GENERATE: 'reports:generate',
  AI_QUERY: 'ai:query',
  USERS_READ: 'users:read',
  USERS_MANAGE: 'users:manage',
  ORG_MANAGE: 'org:manage',
  AUDIT_READ: 'audit:read',
  PLATFORM_ADMIN: 'platform:admin',
  // PR-3 -- espejo de app/core/permissions.py::Permission.DECISION_MANAGE
  // (FA-4B, Decision Lifecycle), necesario para el gate mínimo de
  // aprobar/rechazar una Decision desde StrategicDetailDrawer.tsx (ver
  // PR-3 FINAL REPORT, hallazgo Fase M: los hooks useApproveDecision/
  // useRejectDecision ya existían pero ninguna pantalla los invocaba).
  DECISION_MANAGE: 'decision:manage',
  // UX-2.1 -- espejo de app/core/permissions.py::Permission.STRATEGIC_TOPICS_READ/
  // _MANAGE, identificado como hallazgo pendiente en UX-2.0 (§8/§21:
  // "permissions.ts desincronizado, faltan STRATEGIC_TOPICS_READ/_MANAGE")
  // y marcado ahí mismo como prerrequisito de UX-2.1 antes de que
  // cualquier item de navegación V2 dependa de este permiso.
  STRATEGIC_TOPICS_READ: 'strategic_topics:read',
  STRATEGIC_TOPICS_MANAGE: 'strategic_topics:manage',
} as const

export type Permission = (typeof Permission)[keyof typeof Permission]

export const ROLE_RANK: Record<UserRole, number> = {
  [UserRole.VIEWER]: 0,
  [UserRole.ANALYST]: 1,
  [UserRole.COMPANY_ADMIN]: 2,
  [UserRole.ORG_ADMIN]: 3,
  [UserRole.SUPER_ADMIN]: 4,
}

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.VIEWER]: [
    Permission.COMPANIES_READ,
    Permission.SOURCES_READ,
    Permission.DOCUMENTS_READ,
    Permission.REPORTS_READ,
    // UX-2.1 -- sincronizado con app/core/permissions.py::_VIEWER.
    Permission.STRATEGIC_TOPICS_READ,
  ],
  [UserRole.ANALYST]: [
    Permission.AI_QUERY,
    Permission.REPORTS_GENERATE,
    Permission.SOURCES_MANAGE,
    Permission.DOCUMENTS_MANAGE,
  ],
  [UserRole.COMPANY_ADMIN]: [Permission.COMPANIES_MANAGE],
  [UserRole.ORG_ADMIN]: [
    Permission.USERS_READ,
    Permission.USERS_MANAGE,
    Permission.ORG_MANAGE,
    Permission.AUDIT_READ,
    // UX-2.1 -- sincronizado con app/core/permissions.py::_ORG_ADMIN.
    Permission.STRATEGIC_TOPICS_MANAGE,
    Permission.DECISION_MANAGE,
  ],
  [UserRole.SUPER_ADMIN]: Object.values(Permission),
}

function permissionsOf(role: UserRole): Set<Permission> {
  const rank = ROLE_RANK[role]
  const granted = new Set<Permission>()
  for (const [candidateRole, perms] of Object.entries(ROLE_PERMISSIONS) as [UserRole, Permission[]][]) {
    if (ROLE_RANK[candidateRole] <= rank) {
      perms.forEach((p) => granted.add(p))
    }
  }
  return granted
}

export function hasPermission(role: UserRole | undefined, permission: Permission): boolean {
  if (!role) return false
  return permissionsOf(role).has(permission)
}

/** Un actor solo puede asignar/gestionar roles de rango estrictamente inferior al suyo. */
export function canManageRole(actorRole: UserRole, targetRole: UserRole): boolean {
  return ROLE_RANK[actorRole] > ROLE_RANK[targetRole]
}

export const ASSIGNABLE_ROLES_BY: Record<UserRole, UserRole[]> = Object.fromEntries(
  Object.values(UserRole).map((role) => [
    role,
    Object.values(UserRole).filter((candidate) => canManageRole(role, candidate)),
  ]),
) as Record<UserRole, UserRole[]>
